import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { themeHandler } from "@pineseed/gui/handler/theme-handler";

type ThemePreference = "system" | "light" | "dark";

const LS_KEY_PREFERENCE = "ps-theme-preference";
const LS_KEY_INVERTED = "ps-system-inverted";
const MQ_DARK = "(prefers-color-scheme: dark)";

export interface ThemeContextValue {
  isDarkMode: boolean;
  themePreference: ThemePreference;
  setThemePreference: (pref: ThemePreference) => void;
  systemInverted: boolean;
  setSystemInverted: (inverted: boolean) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readPreference(): ThemePreference {
  if (typeof window === "undefined") return "system";
  const raw = localStorage.getItem(LS_KEY_PREFERENCE);
  if (raw === null) return "system";
  const parsed: unknown = JSON.parse(raw);
  if (parsed === "system" || parsed === "light" || parsed === "dark") {
    return parsed;
  }
  throw new Error(
    `Invalid theme preference in localStorage key "${LS_KEY_PREFERENCE}": ${String(parsed)}`,
  );
}

function readInverted(): boolean {
  if (typeof window === "undefined") return false;
  const raw = localStorage.getItem(LS_KEY_INVERTED);
  if (raw === null) return false;
  const parsed: unknown = JSON.parse(raw);
  if (typeof parsed === "boolean") return parsed;
  throw new Error(
    `Invalid systemInverted in localStorage key "${LS_KEY_INVERTED}": ${String(parsed)}`,
  );
}

function readOsPrefersDark(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia(MQ_DARK).matches;
}

function deriveDarkMode(
  preference: ThemePreference,
  osPrefersDark: boolean,
  systemInverted: boolean,
): boolean {
  if (preference === "system") {
    return systemInverted ? !osPrefersDark : osPrefersDark;
  }
  return preference === "dark";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themePreference, setThemePreference] =
    useState<ThemePreference>(readPreference);
  const [systemInverted, setSystemInverted] = useState<boolean>(readInverted);
  const [osPrefersDark, setOsPrefersDark] = useState<boolean>(readOsPrefersDark);

  const isDarkMode = deriveDarkMode(themePreference, osPrefersDark, systemInverted);

  // Persist themePreference to localStorage on change.
  useEffect(() => {
    localStorage.setItem(LS_KEY_PREFERENCE, JSON.stringify(themePreference));
  }, [themePreference]);

  // Persist systemInverted to localStorage on change.
  useEffect(() => {
    localStorage.setItem(LS_KEY_INVERTED, JSON.stringify(systemInverted));
  }, [systemInverted]);

  // Track OS prefers-color-scheme changes.
  useEffect(() => {
    const mq = window.matchMedia(MQ_DARK);
    const handler = (e: MediaQueryListEvent) => setOsPrefersDark(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Apply theme to DOM whenever resolved dark mode changes.
  useEffect(() => {
    themeHandler.applyTheme(isDarkMode);
  }, [isDarkMode]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      isDarkMode,
      themePreference,
      setThemePreference,
      systemInverted,
      setSystemInverted,
    }),
    [isDarkMode, themePreference, systemInverted],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useThemeContext(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) {
    throw new Error("useThemeContext must be used within a ThemeProvider");
  }
  return ctx;
}
